import React, { useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { TooltipContractData } from '@app-types';
import styles from './MeetingTooltip.module.css';

interface MeetingInvite {
  user_id: number;
  status: string;
}
interface MeetingTooltipProps {
  meeting: { invites?: MeetingInvite[] };
  contracts: TooltipContractData[];
  children: React.ReactNode;
}

// Helper function to find invite status
const getInviteStatus = (userId: number, invites: MeetingInvite[] | undefined): string => {
  const invite = invites?.find((inv) => inv.user_id === userId);
  return invite?.status || 'sent';
};

// Helper function to extract assessor data
const extractAssessor = (
  contracts: TooltipContractData[],
  meeting: { invites?: MeetingInvite[] }
): { name: string; status: string } | null => {
  const assessorContract = contracts.find((c) => c.assessor_name);

  if (!assessorContract?.assessor_name || !assessorContract?.assessor_id) {
    return null;
  }

  return {
    name: assessorContract.assessor_name,
    status: getInviteStatus(assessorContract.assessor_id, meeting.invites)
  };
};

// Helper function to extract unique managers
const extractManagers = (
  contracts: TooltipContractData[],
  meeting: { invites?: MeetingInvite[] }
): Array<{ id: number; name: string; status: string }> => {
  const managersMap = new Map<number, { id: number; name: string; status: string }>();

  contracts.forEach((contract) => {
    if (!contract.contract_managers || !Array.isArray(contract.contract_managers)) {
      return;
    }

    contract.contract_managers.forEach((manager) => {
      if (!manager || managersMap.has(manager.id)) {
        return;
      }

      managersMap.set(manager.id, {
        id: manager.id,
        name: manager.name,
        status: getInviteStatus(manager.id, meeting.invites)
      });
    });
  });

  return Array.from(managersMap.values());
};

const MeetingTooltip: React.FC<MeetingTooltipProps> = ({ meeting, contracts, children }) => {
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Use the meeting data directly from props (no polling)
  const currentMeeting = meeting;

  // Collect all unique participants from all contracts
  const participantsData = useMemo(() => {
    if (!currentMeeting) {
      return { assessor: null, managers: [] };
    }

    return {
      assessor: extractAssessor(contracts, currentMeeting),
      managers: extractManagers(contracts, currentMeeting)
    };
  }, [contracts, currentMeeting]);

  // Check if meeting has required data
  if (!currentMeeting || (!participantsData.assessor && participantsData.managers.length === 0)) {
    return <>{children}</>;
  }

  const updateTooltipPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + rect.width / 2
      });
    }
  };

  const handleMouseEnter = () => {
    updateTooltipPosition();
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const getStatusIcon = (status: string) => {
    let icon;
    let iconClass;
    switch (status) {
      case 'accepted':
        icon = '✓'; // зелена галочка
        iconClass = styles.meetingTooltipStatusIconAccepted;
        break;
      case 'declined':
        icon = '✕'; // червоний хрестик
        iconClass = styles.meetingTooltipStatusIconDeclined;
        break;
      case 'sent':
      default:
        icon = '?'; // сірий знак питання
        iconClass = styles.meetingTooltipStatusIconSent;
        break;
    }

    return <span className={clsx(styles.meetingTooltipStatusIcon, iconClass)}>{icon}</span>;
  };

  return (
    <>
      <div
        className={styles.meetingTooltipTrigger}
        ref={triggerRef}
        role="tooltip"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {isVisible &&
        createPortal(
          <div
            className={styles.meetingTooltipContent}
            role="tooltip"
            style={{
              position: 'fixed',
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: 'translateX(-50%)',
              zIndex: 999999
            }}
          >
            <div className={styles.meetingTooltipTitle}>Meeting Participants</div>
            <div className={styles.meetingTooltipParticipants}>
              {/* Assessor - simple inline display */}
              {participantsData.assessor && (
                <div className={styles.meetingTooltipParticipant}>
                  <span className={styles.meetingTooltipParticipantRole}>Assessor:</span>
                  <div className={styles.meetingTooltipParticipantInfo}>
                    <span className={styles.meetingTooltipParticipantName}>{participantsData.assessor.name}</span>
                    <span className={styles.meetingTooltipStatus}>
                      {getStatusIcon(participantsData.assessor.status)}
                    </span>
                  </div>
                </div>
              )}

              {/* Managers grouped together */}
              {participantsData.managers.length > 0 && (
                <div className={styles.meetingTooltipParticipantGroup}>
                  <span className={styles.meetingTooltipParticipantRole}>Manager(s):</span>
                  <div className={styles.meetingTooltipParticipantList}>
                    {participantsData.managers.map((manager) => (
                      <div key={`manager-${manager.id}`} className={styles.meetingTooltipParticipantInfo}>
                        <span className={styles.meetingTooltipParticipantName}>{manager.name}</span>
                        <span className={styles.meetingTooltipStatus}>{getStatusIcon(manager.status)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default MeetingTooltip;
